import { useState } from 'react'

import classNames from 'classnames'
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik'
import {
  AddAuthorizationsAndInvites,
  AddAuthorizationsAndInvitesVariables,
  FetchElementAuthorizations_knownUsers,
  CurrentUserEmailAddresses_user_emails,
} from 'globalTypes'
import { filter, startsWith, toLower, words } from 'lodash'
import Autosuggest from 'react-autosuggest'
import TagsInput from 'react-tagsinput'

import { useMutation } from '@apollo/client'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Button from 'Root/App/Form/Button'
import { MAX_TAGS } from 'lib/constants'

import Suggestion from './Suggestion'
import Tag from './Tag'
import MUTATION from './add-authorizations-and-invites.graphql'
import autosuggest from './autosuggest.module.css'
import { submit, validationSchema } from './form'
import styles from './index.module.css'

interface Props {
  id: string
  knownUsers: FetchElementAuthorizations_knownUsers[]
  creator: {
    id: string
    email: string
    __typename: string
    bio: string
    name: string
    picture: string
    website: string
  }
  currentUserData: CurrentUserEmailAddresses_user_emails[]
}

const AddUser = ({ id, knownUsers, creator, currentUserData }: Props) => {
  const { _ } = useLingui()
  const [shareEmailError, setShareEmailError] = useState<string | null>(null)
  // This should be the slug of the resource that will be shared(eg. collection, content etc...)
  // but it's the id, we need to fix it
  const sharedItemID = id

  const [mutate] = useMutation<
    AddAuthorizationsAndInvites,
    AddAuthorizationsAndInvitesVariables
  >(MUTATION, {
    refetchQueries: ['FetchElementAuthorizations'],
  })

  function renderInput({ addTag, ...props }) {
    const handleOnChange = (e, { method }) => {
      if (method === 'enter') {
        e.preventDefault()
      } else {
        props.onChange(e)
      }
    }

    const suggestions = filter(
      knownUsers || [],
      ({ name, email }) =>
        startsWith(toLower(name), toLower(props.value)) ||
        startsWith(toLower(email), toLower(props.value))
    )

    const shouldRenderSuggestions = (value) =>
      Boolean(value) && value.trim().length > 0

    return (
      <Autosuggest
        getSuggestionValue={(suggestion) => suggestion.email}
        inputProps={{ ...props, onChange: handleOnChange }}
        onSuggestionsClearRequested={() => {}}
        onSuggestionSelected={(_e, { suggestion }) => addTag(suggestion.email)}
        onSuggestionsFetchRequested={() => {}}
        ref={props.ref}
        renderSuggestion={Suggestion}
        shouldRenderSuggestions={shouldRenderSuggestions}
        suggestions={suggestions}
        theme={autosuggest}
      />
    )
  }
  return (
    <Formik
      initialValues={{ emails: [] }}
      enableReinitialize
      onSubmit={submit(
        mutate,
        id,
        currentUserData,
        creator.email,
        sharedItemID
      )}
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {(formikProps: FormikProps<FormikValues>) => (
        <Form className={styles.form}>
          <Field name="emails">
            {({ field, form }) => {
              const { name, value } = field
              const { setFieldValue, setFieldTouched } = form
              const maxTagsReached = value.length === MAX_TAGS

              const onChange = (value) => {
                if (value.length <= MAX_TAGS) {
                  setShareEmailError(null)
                  const trimmedEmails = value.map((v) => v.trim())

                  setFieldValue(name, trimmedEmails)
                  setFieldTouched(name, true)
                } else {
                  setShareEmailError(
                    _(
                      msg`Puoi invitare al massimo ${MAX_TAGS} persone per volta.`
                    )
                  )
                }
              }

              const renderTag = ({ key, ...props }) => (
                <Tag {...props} index={key} key={key} creator={creator} />
              )

              return (
                <div className="flex flex-col gap-1 mb-4">
                  <TagsInput
                    addKeys={[9, 13, 32, 59, 108, 188, 222]}
                    addOnBlur
                    addOnPaste
                    className={styles.tagsInput}
                    inputProps={{
                      placeholder: !maxTagsReached
                        ? _(msg`Invita o aggiungi indirizzi email...`)
                        : '',
                      disabled: maxTagsReached,
                    }}
                    onChange={onChange}
                    onlyUnique
                    pasteSplit={(data) => words(data, /(.+\@.+\..+)/gi)}
                    renderInput={renderInput}
                    renderTag={renderTag}
                    validationRegex={/^(.+\@.+\..+)$/i}
                    value={value}
                  />
                  <div className="flex items-center w-full">
                    {shareEmailError && (
                      <span className="text-red-500 text-left">
                        {shareEmailError}
                      </span>
                    )}
                    <label
                      className={classNames([
                        'text-right text-sm ml-auto',
                        {
                          'text-red-500': maxTagsReached,
                          'text-gray-500': !maxTagsReached,
                        },
                      ])}
                    >
                      {value?.length ?? 0}/{MAX_TAGS}
                    </label>
                  </div>
                </div>
              )
            }}
          </Field>

          <Button
            disabled={!formikProps.dirty || !formikProps.isValid}
            processing={formikProps.isSubmitting}
            size="XS"
            type="submit"
          >
            <Trans>Condividi</Trans>
          </Button>
        </Form>
      )}
    </Formik>
  )
}

export default AddUser
